import { yupResolver } from "@hookform/resolvers/yup";
import CheckIcon from "@mui/icons-material/Check";
import { Box, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { PhoneInput } from "Components/_Shared/Inputs/PhoneInput";
import { BlDropdown } from "Forms/Components/BlDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { type NewAnalysisFormData, newAnalysisFormSchema } from "Forms/Schemas/newAnalysisFormSchema";
import {
  type CreateClientMutationError,
  useCreateAnalysis,
  useCreateClient,
  useListCountries,
  useLookupClientByBirthNumber,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  BirthNumberLookupResultStatus,
  CreateAnalysisBadRequestErrorCode,
  CreateClientBadRequestErrorCode,
  CreateMangoClientBodyType,
  type SearchedClient,
} from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getMinBirthDate, parseBirthNumber } from "Infrastructure/Utils/ClientUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FoundClientDialog } from "./FoundClientDialog";
import { getMissingMangoAddressFields, toCreateLocalClientBody, toCreateMangoClientBody } from "./NewAnalysisForm.helpers";

interface NewAnalysisFormProps {
  onCancel: () => void;
  onClientSelect: (client: SearchedClient) => void;
}

const emptyFormValues: NewAnalysisFormData = {
  firstName: "",
  lastName: "",
  titlePrefix: "",
  titlePostfix: "",
  birthDate: "" as any,
  email: "",
  phone: "",
  prefix: "+420",
  countryCode: "CZE",
  city: "",
  postalCode: "",
  street: "",
  streetNumber: "",
  houseNumber: "",
  personalIdentificationNumber: "",
  createMangoClient: false,
};

export const NewAnalysisForm: React.FunctionComponent<NewAnalysisFormProps> = ({ onCancel, onClientSelect }) => {
  const { t } = useResource();
  const isCreateMangoClientEnabled = useFeatureFlag(FeatureFlag.CreateMangoClient);
  // Without the flag the switch stays hidden and off, so only a local client can be created.
  const defaultFormValues = useMemo<NewAnalysisFormData>(
    () => ({ ...emptyFormValues, createMangoClient: isCreateMangoClientEnabled }),
    [isCreateMangoClientEnabled],
  );
  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<NewAnalysisFormData>({
    resolver: yupResolver(newAnalysisFormSchema(t)),
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });

  const navigate = useNavigate();

  const { data: countriesData } = useListCountries();

  const createMangoClient = useWatch({ control, name: "createMangoClient" });
  const birthNumber = parseBirthNumber(useWatch({ control, name: "personalIdentificationNumber" }));

  const {
    mutate: lookupClientByBirthNumber,
    data: lookupResult,
    variables: lookupVariables,
    isPending: isLookupPending,
    isError: isLookupError,
    reset: resetLookup,
  } = useLookupClientByBirthNumber({
    mutation: {
      onError: () => {
        toast.error(t(Resources.NewAnalysis.Toast.BirthNumberLookupError));
      },
    },
  });

  // A lookup result only holds for the birth number it was made for - editing the field makes it stale.
  const isLookupForCurrentBirthNumber = birthNumber !== null && lookupVariables?.data.birthNumber === birthNumber.formatted;
  const lookupStatus = isLookupForCurrentBirthNumber ? lookupResult?.status : undefined;
  const isLookingUp = isLookupForCurrentBirthNumber && isLookupPending;
  const foundClient = lookupStatus === BirthNumberLookupResultStatus.found ? (lookupResult?.client ?? null) : null;

  const lookupBirthNumber = (formattedBirthNumber: string) => {
    lookupClientByBirthNumber({ data: { birthNumber: formattedBirthNumber } });
  };

  const handleBirthNumberChange = (value: string) => {
    const parsed = parseBirthNumber(value);
    if (!parsed) return;

    if (parsed.birthDate) {
      setValue("birthDate", parsed.birthDate, { shouldValidate: true });
    }
    if (parsed.formatted !== lookupVariables?.data.birthNumber || isLookupError) {
      lookupBirthNumber(parsed.formatted);
    }
  };

  const handleClearBirthNumber = () => {
    setValue("personalIdentificationNumber", "");
    resetLookup();
  };

  const handleCreateMangoClientChange = (checked: boolean) => {
    if (!checked) {
      handleClearBirthNumber();
      clearErrors("personalIdentificationNumber");
    }
    // The birth number is required only with the switch on - re-validate so `isValid` follows it.
    trigger("createMangoClient");
  };

  const {
    mutate: createAnalysis,
    data: newAnalysis,
    isPending: isCreateAnalysisPending,
  } = useCreateAnalysis({
    mutation: {
      onError: (error) => {
        toast.error(
          error?.code === CreateAnalysisBadRequestErrorCode.nsure_self_dealing_forbidden
            ? t(Resources.NewAnalysis.Toast.NsureSelfDealingForbidden)
            : t(Resources.NewAnalysis.Toast.CreateAnalysisError),
        );
      },
    },
  });

  const getCreateClientErrorMessage = (code: CreateClientMutationError["code"] | undefined) => {
    switch (code) {
      case CreateClientBadRequestErrorCode.client_already_exists:
        return t(Resources.NewAnalysis.Toast.ClientAlreadyExists);
      case CreateClientBadRequestErrorCode.invalid_birth_number:
        return t(Resources.NewAnalysis.Toast.InvalidBirthNumber);
      case CreateClientBadRequestErrorCode.unsupported_address_country:
        return t(Resources.NewAnalysis.Toast.UnsupportedAddressCountry);
      default:
        return t(Resources.NewAnalysis.Toast.CreateClientError);
    }
  };

  const { mutate: createClient, isPending: isCreateClientPending } = useCreateClient({
    mutation: {
      onSuccess: (newClient) => {
        createAnalysis({ data: { clientGuid: newClient.guid } });
      },
      onError: (error, { data }) => {
        toast.error(getCreateClientErrorMessage(error?.code));
        // The client may have been created in MangoUp since the lookup (e.g. by an earlier attempt whose analysis
        // then failed) - looking it up again brings up the dialog for continuing with it.
        if (error?.code === CreateClientBadRequestErrorCode.client_already_exists && data.type === CreateMangoClientBodyType.mango) {
          lookupBirthNumber(data.personalIdentificationNumber);
        }
      },
    },
  });

  const isCreating = isCreateClientPending || isCreateAnalysisPending;
  const canCreate = isValid && (!createMangoClient || lookupStatus === BirthNumberLookupResultStatus.not_found);

  useEffect(() => {
    if (newAnalysis) {
      navigate(AppRouting.AnalysisDetail.path(newAnalysis.analysisId, newAnalysis.versionId));
    }
  }, [newAnalysis, navigate]);

  useEffect(() => {
    reset(defaultFormValues);
    clearErrors();
  }, [reset, clearErrors, defaultFormValues]);

  const onCreateNewAnalysis = (data: NewAnalysisFormData) => {
    if (!data.createMangoClient) {
      createClient({ data: toCreateLocalClientBody(data) });
      return;
    }

    const missingAddressFields = getMissingMangoAddressFields(data);
    if (missingAddressFields.length > 0) {
      for (const field of missingAddressFields) {
        setError(field, { message: t(Resources.Validation.Required) });
      }
      return;
    }

    createClient({ data: toCreateMangoClientBody(data) });
  };

  const handleClear = () => {
    reset(defaultFormValues);
    clearErrors();
    resetLookup();
    onCancel();
  };

  return (
    <Box
      boxShadow={3}
      p={Spacing.XXL}
      sx={(theme) => ({
        background: theme.colors.white,
        borderRadius: theme.spacing(Spacing.XXXL),
      })}
    >
      <form>
        <Grid container columnSpacing={Spacing.XL} rowSpacing={Spacing.XL}>
          {isCreateMangoClientEnabled && (
            <Grid size={12}>
              <FormSwitch
                control={control}
                name="createMangoClient"
                label={t(Resources.NewAnalysis.Form.Field.CreateMangoClient)}
                errors={errors}
                onChange={handleCreateMangoClientChange}
              />
            </Grid>
          )}
          {createMangoClient && (
            <Grid size={12}>
              <BlFormInput
                control={control}
                name="personalIdentificationNumber"
                label={t(Resources.NewAnalysis.Form.Field.PersonalIdentificationNumber)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
                mask="000000/000[0]"
                onChange={handleBirthNumberChange}
                inputEndAdornment={
                  isLookingUp ? (
                    <CircularProgress size={20} />
                  ) : lookupStatus === BirthNumberLookupResultStatus.not_found ? (
                    <CheckIcon sx={{ color: "success.main" }} />
                  ) : undefined
                }
              />
              {lookupStatus === BirthNumberLookupResultStatus.not_accessible && (
                <Alert variant="warning" text={t(Resources.NewAnalysis.BirthNumberLookup.NotAccessible)} />
              )}
            </Grid>
          )}
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="firstName"
              label={t(Resources.NewAnalysis.Form.Field.FirstName)}
              errors={errors}
              labelEndAdornment={<StyledLabelAdornment />}
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="lastName"
              label={t(Resources.NewAnalysis.Form.Field.LastName)}
              labelEndAdornment={<StyledLabelAdornment />}
              errors={errors}
            />
          </Grid>
          <Grid size={3}>
            <BlFormInput
              control={control}
              name="titlePrefix"
              label={t(Resources.NewAnalysis.Form.Field.AcademicDegreePrefix)}
              errors={errors}
            />
          </Grid>
          <Grid size={3}>
            <BlFormInput
              control={control}
              name="titlePostfix"
              label={t(Resources.NewAnalysis.Form.Field.AcademicDegreePostfix)}
              errors={errors}
            />
          </Grid>
          <Grid size={6}>
            <DatePicker
              control={control}
              name="birthDate"
              label={t(Resources.NewAnalysis.Form.Field.BirthDate)}
              labelEndAdornment={<StyledLabelAdornment />}
              errors={errors}
              minDate={getMinBirthDate()}
              disableFuture
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="email"
              labelEndAdornment={<StyledLabelAdornment />}
              errors={errors}
              label={t(Resources.Common.Email)}
            />
          </Grid>
          <Grid size={6}>
            <PhoneInput
              control={control}
              numberInputName="phone"
              prefixInputName="prefix"
              label={t(Resources.Common.Phone)}
              errors={errors}
              labelEndAdornment={<StyledLabelAdornment />}
            />
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
        </Grid>
        <Box mt={Spacing.XL}>
          <Grid container spacing={Spacing.XL}>
            <Grid size={12}>
              <Typography fontWeight={600} variant="subtitle1">
                {t(Resources.NewAnalysis.Address)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <BlFormInput control={control} name="street" label={t(Resources.NewAnalysis.Form.Field.Street)} errors={errors} />
            </Grid>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="houseNumber"
                label={t(Resources.NewAnalysis.Form.Field.HouseNumber)}
                errors={errors}
                mask={/^\d+$/}
              />
            </Grid>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="streetNumber"
                label={t(Resources.NewAnalysis.Form.Field.StreetNumber)}
                errors={errors}
                mask={/^\d+$/}
              />
            </Grid>
            <Grid size={6}>
              <BlFormInput control={control} name="city" label={t(Resources.NewAnalysis.Form.Field.City)} errors={errors} />
            </Grid>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="postalCode"
                label={t(Resources.NewAnalysis.Form.Field.PostalCode)}
                errors={errors}
                mask="000 00"
              />
            </Grid>
            <Grid size={12}>
              <BlDropdown
                control={control}
                name="countryCode"
                label={t(Resources.NewAnalysis.Form.Field.Country)}
                errors={errors}
                codeList={countriesData || []}
              />
            </Grid>
          </Grid>
        </Box>
        <Flex pt={Spacing.XL} justifyContent="space-between" textAlign="right">
          <Button color="secondary" onClick={handleClear}>
            {t(Resources.Common.Cancel)}
          </Button>

          <Button color="primary" disabled={!canCreate} loading={isCreating} onClick={handleSubmit(onCreateNewAnalysis)} type="submit">
            {t(Resources.Common.Create)}
          </Button>
        </Flex>
      </form>

      <FoundClientDialog client={foundClient} onContinue={onClientSelect} onClearBirthNumber={handleClearBirthNumber} />
    </Box>
  );
};
